import {useCallback, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {useDialogSlot} from 'UI/hooks/useDialogManager';

const DIALOG_PRIORITY = 1;

/**
 * Custom hook for the `AppUpdateBanner` component.
 * Detects when a new service worker is waiting to activate
 * and provides a function to trigger the update + reload.
 * Coordinates with the shared `DialogManagerProvider` (see `useDialogManager`) so it doesn't
 * overlap with other global dialogs/banners, such as the PWA install prompt.
 *
 * @returns An object containing:
 * - `applyUpdate`: Tells the waiting SW to skip waiting and reloads the page.
 * - `hasUpdate`: Whether a new SW version is waiting and this banner is allowed to show it.
 * - `t`: The translation function from `react-i18next`, reused by the `AppUpdateBanner` component
 * so it stays limited to this single hook call (see `@nfq/component-single-hook`).
 */
export const useAppUpdateBanner = () => {
    const {t} = useTranslation();
    const [hasUpdate, setHasUpdate] = useState(false);
    const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        /** Registers the SW and listens for updates. */
        const setup = async () => {
            let registration: ServiceWorkerRegistration;

            try {
                // Rejects e.g. under Google's Web Rendering Service, which blocks SW
                // registration by design — must be caught or it surfaces as an
                // unhandled rejection during crawling.
                registration = await navigator.serviceWorker.register('/sw.js');
            } catch {
                return;
            }

            /**
             * Handles a new service worker entering the waiting state.
             *
             * @param sw The waiting service worker.
             */
            const onWaiting = (sw: ServiceWorker | null) => {
                if (sw) {
                    setWaitingSW(sw);
                    setHasUpdate(true);
                }
            };

            // Check if there's already a waiting SW
            if (registration.waiting) {
                onWaiting(registration.waiting);
            }

            // Listen for new SW installations
            registration.addEventListener('updatefound', () => {
                const newSW = registration.installing;

                if (!newSW) return;

                newSW.addEventListener('statechange', () => {
                    if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
                        onWaiting(newSW);
                    }
                });
            });
        };

        void setup();
    }, []);

    /**
     * Activates the waiting service worker and reloads the page.
     */
    const applyUpdate = useCallback(() => {
        if (!waitingSW) return;

        waitingSW.postMessage({type: 'SKIP_WAITING'});

        // Reload once the new SW takes control
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        }, {once: true});
    }, [waitingSW]);

    const isActive = useDialogSlot('appUpdateBanner', DIALOG_PRIORITY, hasUpdate);

    return {
        applyUpdate,
        hasUpdate: hasUpdate && isActive,
        t
    };
};